import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';

const MSG91_WIDGET_ID   = process.env.REACT_APP_MSG91_WIDGET_ID  || '366579716455303737343036';
const MSG91_TOKEN_AUTH  = process.env.REACT_APP_MSG91_TOKEN_AUTH || '519207TlSvjQX9QgmM6a148befP1';

const msg91Widget = async (endpoint, body) => {
  const res = await fetch(`https://widget.msg91.com/api/v1/widget/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'widget-id':   MSG91_WIDGET_ID,
      'token-auth':  MSG91_TOKEN_AUTH,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (data.type !== 'success') throw new Error(data.message || `OTP ${endpoint} failed`);
  return data;
};

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [inputType,    setInputType]    = useState('mobile'); // 'mobile' | 'email'
  const [identifier,   setIdentifier]   = useState('');
  const [otp,          setOtp]          = useState('');
  const [newPassword,  setNewPassword]  = useState('');
  const [confirmPass,  setConfirmPass]  = useState('');
  const [showPass,     setShowPass]     = useState(false);
  const [step,         setStep]         = useState(1); // 1 | 2 | 3
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [success,      setSuccess]      = useState('');
  const [reqId,        setReqId]        = useState('');  // MSG91 request ID
  const [msg91Token,   setMsg91Token]   = useState(''); // MSG91 access token

  // Auto-detect whether user is typing email or mobile
  const handleIdentifierChange = (val) => {
    setIdentifier(val);
    setError('');
    if (val.includes('@')) {
      setInputType('email');
    } else if (/^\d/.test(val)) {
      setInputType('mobile');
    }
  };

  const getPayload = () =>
    inputType === 'email' ? { email: identifier } : { mobile: identifier };

  const maskedIdentifier = () => {
    if (inputType === 'email') {
      const [user, domain] = identifier.split('@');
      return user.slice(0, 2) + '****@' + domain;
    }
    return '+91 ' + identifier.slice(0, 2) + '******' + identifier.slice(-2);
  };

  // Step 1 — Check user exists and send OTP via backend
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (inputType === 'mobile' && identifier.replace(/\D/g,'').length !== 10) {
      return setError('Enter a valid 10-digit mobile number.');
    }
    if (inputType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
      return setError('Enter a valid email address.');
    }
    setLoading(true);
    try {
      await api.post('/api/auth/forgot-password', getPayload());
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — Verify OTP via backend
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length < 4) return setError('Enter the complete OTP.');
    setLoading(true);
    try {
      await api.post('/api/auth/verify-otp', { ...getPayload(), otp });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Invalid or expired OTP.');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP handler (used by the "Resend code" button)
  const handleResendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      await api.post('/api/auth/forgot-password', getPayload());
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3 — Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) return setError('Password must be at least 6 characters.');
    if (newPassword !== confirmPass) return setError('Passwords do not match.');
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { ...getPayload(), newPassword });
      setSuccess('Password reset successful! Redirecting to login…');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = ['Contact', 'Verify OTP', 'New Password'];

  return (
    <div className="fixed inset-0 font-body">

      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          className="w-full h-full object-cover brightness-75"
          src="https://lh3.googleusercontent.com/aida-public/AB6AXuDfsl01tTn-hVDG7p-WvDCVX3XdN_BGl1NvnPvkgV5vwh0JWPoCa7GXgE6dDRnDTrNey4uw-t6iHSW5ufw7qjCw5_IMW_E6PQGHubjCpmuGL9m4uC87jkpdIE00XlDaJpJKYraCCrQJ0_LaqbKVg1qck_8PrllLS3TV3L_Aw3QkftgJIHIbK7JMD4oqLHC2JyisWL22tulMprWo3pEZ0kQBgKYMZWbk8RlMlXXjKJ7zBoEb7Iks8DnFMFjzWeYHrONEZ7a_JXv8NsCj"
          alt="" aria-hidden="true"
        />
        <div className="absolute inset-0 backdrop-blur-md bg-black/35" />
      </div>

      <div
        className="relative z-10 h-full flex items-center justify-center px-4"
        onClick={() => navigate('/')}
      >
        <div
          className="w-full max-w-3xl bg-white rounded-2xl overflow-hidden flex shadow-2xl"
          style={{ maxHeight: '96vh' }}
          onClick={(e) => e.stopPropagation()}
        >

          {/* ── Left image panel ─────────────────────────────────── */}
          <div className="hidden md:block md:w-[52%] relative flex-shrink-0">
            <img
              alt="Champion Goat"
              className="absolute inset-0 w-full h-full object-cover object-center"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuDS-o60_c7U6hne0R0xpVP3atsjAGKL2mkrEoW_xlnWI2pFpaMM_lSS4JlqusfK2r2tJwRkIrjVSFy2vFlgx3d8iSLu7RdjOF3z3fAe1YJO02fRFufoXEoKT8cxrBIT8X_VBuNwGlPzzHoBWUrFiK-eCVctMhIHUiEKQ13TqlWoSTPZjodPIWgpcp5B7Soz30JDGaUhqT5xysUoaX3E6wWjkNk3RojcKAPNDsKQakpG_3FXDuD4TZvY2lCHPTIKoxpk4ZXCIKVdJ2Uc"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 space-y-3">
              <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[10px] font-bold tracking-widest uppercase">
                SECURE RECOVERY
              </span>
              <h2 className="font-headline text-lg font-extrabold text-white leading-tight tracking-tight">
                Account Recovery
              </h2>
              <p className="text-white/75 text-xs font-medium leading-relaxed">
                We'll send a one-time code to verify your identity before resetting your password.
              </p>
              {/* What's accepted */}
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 space-y-2 mt-2">
                <p className="text-white/90 text-[10px] font-bold uppercase tracking-widest mb-1">Accepted identifiers</p>
                <div className="flex items-center gap-2 text-white/80 text-[11px]">
                  <span className="material-symbols-outlined text-sm text-emerald-300">phone_iphone</span>
                  10-digit Indian mobile number
                </div>
                <div className="flex items-center gap-2 text-white/80 text-[11px]">
                  <span className="material-symbols-outlined text-sm text-emerald-300">mail</span>
                  Registered email address
                </div>
              </div>
            </div>
          </div>

          {/* ── Right form panel ──────────────────────────────────── */}
          <div className="flex-1 flex flex-col p-5 overflow-y-auto">

            {/* Close */}
            <div className="flex justify-end mb-2">
              <button
                onClick={() => navigate('/')}
                className="text-outline hover:text-on-surface p-1 rounded-full hover:bg-surface-container-low transition-colors"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>close</span>
              </button>
            </div>

            {/* Logo + heading */}
            <div className="mb-4">
              <img src="/assets/elite-goats.png" alt="Elite Goats" className="h-7 w-auto object-contain mb-3" />
              <h1 className="font-headline text-base font-bold text-on-surface tracking-tight">
                {step === 1 && 'Reset your password'}
                {step === 2 && 'Enter verification code'}
                {step === 3 && 'Create new password'}
              </h1>
              <p className="text-on-surface-variant text-[11px] mt-0.5">
                {step === 1 && 'Use your mobile number or email to receive a code.'}
                {step === 2 && `Code sent to ${maskedIdentifier()}.`}
                {step === 3 && 'Choose a strong password for your account.'}
              </p>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-1 mb-5">
              {stepLabels.map((label, i) => {
                const s = i + 1;
                const done    = step > s;
                const current = step === s;
                return (
                  <React.Fragment key={s}>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-300 ${
                        done    ? 'bg-primary text-white' :
                        current ? 'bg-primary text-white ring-2 ring-primary/30' :
                                  'bg-surface-container text-outline'
                      }`}>
                        {done
                          ? <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>check</span>
                          : s}
                      </div>
                      <span className={`text-[10px] font-semibold hidden sm:block ${current ? 'text-primary' : 'text-outline'}`}>
                        {label}
                      </span>
                    </div>
                    {s < 3 && (
                      <div className={`flex-1 h-px mx-1 transition-all duration-500 ${step > s ? 'bg-primary' : 'bg-surface-container-high'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Alerts */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-[11px] mb-3 flex items-start gap-2">
                <span className="material-symbols-outlined text-sm mt-0.5">error</span>
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-[11px] mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">check_circle</span>
                {success}
              </div>
            )}

            {/* ── STEP 1 — Identifier input ─────────────────────── */}
            {step === 1 && (
              <form onSubmit={handleSendOtp} className="flex flex-col gap-3">

                {/* Toggle tabs */}
                <div className="flex bg-surface-container rounded-xl p-1 gap-1">
                  <button
                    type="button"
                    onClick={() => { setInputType('mobile'); setIdentifier(''); setError(''); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      inputType === 'mobile'
                        ? 'bg-white shadow text-primary'
                        : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    <span className="material-symbols-outlined text-sm">phone_iphone</span>
                    Mobile
                  </button>
                  <button
                    type="button"
                    onClick={() => { setInputType('email'); setIdentifier(''); setError(''); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      inputType === 'email'
                        ? 'bg-white shadow text-primary'
                        : 'text-on-surface-variant hover:text-on-surface'
                    }`}
                  >
                    <span className="material-symbols-outlined text-sm">mail</span>
                    Email
                  </button>
                </div>

                {/* Mobile input */}
                {inputType === 'mobile' && (
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-0.5">
                      Mobile Number
                    </label>
                    <div className="relative flex items-center bg-surface-container-low border border-outline-variant/30 rounded-lg focus-within:ring-1 focus-within:ring-primary focus-within:bg-white transition-all">
                      <span className="pl-3 text-[10px] font-bold text-outline border-r border-outline-variant/30 pr-2 py-2 select-none">
                        +91
                      </span>
                      <input
                        type="tel"
                        value={identifier}
                        onChange={(e) => handleIdentifierChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="flex-1 px-2 py-1.5 bg-transparent outline-none placeholder:text-outline-variant text-xs"
                        placeholder="98765 43210"
                        pattern="[0-9]{10}"
                        required
                      />
                      {identifier.length === 10 && (
                        <span className="material-symbols-outlined text-emerald-500 text-sm pr-3">check_circle</span>
                      )}
                    </div>
                    <p className="text-[10px] text-outline mt-1">
                      OTP will be sent via SMS to this number.
                    </p>
                  </div>
                )}

                {/* Email input */}
                {inputType === 'email' && (
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-0.5">
                      Email Address
                    </label>
                    <div className="relative flex items-center bg-surface-container-low border border-outline-variant/30 rounded-lg focus-within:ring-1 focus-within:ring-primary focus-within:bg-white transition-all">
                      <span className="material-symbols-outlined text-outline text-base pl-3">mail</span>
                      <input
                        type="email"
                        value={identifier}
                        onChange={(e) => handleIdentifierChange(e.target.value)}
                        className="flex-1 px-2 py-1.5 bg-transparent outline-none placeholder:text-outline-variant text-xs"
                        placeholder="you@example.com"
                        required
                      />
                      {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier) && (
                        <span className="material-symbols-outlined text-emerald-500 text-sm pr-3">check_circle</span>
                      )}
                    </div>
                    <p className="text-[10px] text-outline mt-1">
                      OTP will be sent to this email address.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !identifier}
                  className="w-full py-2 bg-gradient-to-r from-primary to-primary-container text-white font-headline font-bold text-xs rounded-xl shadow-md flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending…</>
                    : <><span>Send Verification Code</span><span className="material-symbols-outlined text-sm">arrow_forward</span></>
                  }
                </button>
              </form>
            )}

            {/* ── STEP 2 — OTP input ────────────────────────────── */}
            {step === 2 && (
              <form onSubmit={handleVerifyOtp} className="flex flex-col gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-0.5">
                    Verification Code
                  </label>
                  {/* OTP destination badge */}
                  <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2 mb-3">
                    <span className="material-symbols-outlined text-primary text-sm">
                      {inputType === 'email' ? 'mail' : 'phone_iphone'}
                    </span>
                    <span className="text-[11px] text-on-surface">
                      Code sent to <strong>{maskedIdentifier()}</strong>
                    </span>
                  </div>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => { setOtp(e.target.value.replace(/\D/g,'').slice(0,6)); setError(''); }}
                    className="w-full px-3 py-3 bg-surface-container-low border border-outline-variant/30 rounded-lg focus:ring-1 focus:ring-primary focus:bg-white transition-all outline-none text-center text-xl font-black tracking-[0.5em] placeholder:tracking-normal placeholder:text-outline-variant placeholder:text-sm placeholder:font-normal"
                    placeholder="000000"
                    inputMode="numeric"
                    maxLength={6}
                    required
                  />
                  <p className="text-[10px] text-outline mt-1 text-center">
                    Enter the 6-digit code. Valid for 10 minutes.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length < 4}
                  className="w-full py-2 bg-gradient-to-r from-primary to-primary-container text-white font-headline font-bold text-xs rounded-xl shadow-md flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Verifying…</>
                    : 'Verify Code'
                  }
                </button>

                <div className="flex items-center justify-between text-[11px]">
                  <button type="button" onClick={() => { setStep(1); setOtp(''); setError(''); }}
                    className="text-primary font-bold hover:underline flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">arrow_back</span>
                    Change {inputType === 'email' ? 'email' : 'number'}
                  </button>
                  <button type="button" onClick={handleResendOtp}
                    className="text-on-surface-variant hover:text-primary transition-colors">
                    Resend code
                  </button>
                </div>
              </form>
            )}

            {/* ── STEP 3 — New password ─────────────────────────── */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="flex flex-col gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-0.5">
                    New Password
                  </label>
                  <div className="relative flex items-center bg-surface-container-low border border-outline-variant/30 rounded-lg focus-within:ring-1 focus-within:ring-primary focus-within:bg-white transition-all">
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                      className="flex-1 px-3 py-1.5 bg-transparent outline-none placeholder:text-outline-variant text-xs"
                      placeholder="Min. 6 characters"
                      minLength={6}
                      required
                    />
                    <button type="button" onClick={() => setShowPass(!showPass)}
                      className="pr-3 text-outline hover:text-on-surface transition-colors">
                      <span className="material-symbols-outlined text-sm">
                        {showPass ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {/* Strength indicator */}
                  {newPassword.length > 0 && (
                    <div className="mt-1.5 flex gap-1">
                      {[1,2,3,4].map((i) => (
                        <div key={i} className={`h-1 flex-1 rounded-full transition-all ${
                          newPassword.length >= i * 3
                            ? newPassword.length >= 10 ? 'bg-emerald-500'
                            : newPassword.length >= 6  ? 'bg-amber-400'
                            : 'bg-red-400'
                            : 'bg-surface-container-high'
                        }`} />
                      ))}
                      <span className="text-[10px] text-outline ml-1">
                        {newPassword.length < 6 ? 'Weak' : newPassword.length < 10 ? 'Fair' : 'Strong'}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-0.5">
                    Confirm Password
                  </label>
                  <div className="relative flex items-center bg-surface-container-low border border-outline-variant/30 rounded-lg focus-within:ring-1 focus-within:ring-primary focus-within:bg-white transition-all">
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={confirmPass}
                      onChange={(e) => { setConfirmPass(e.target.value); setError(''); }}
                      className="flex-1 px-3 py-1.5 bg-transparent outline-none placeholder:text-outline-variant text-xs"
                      placeholder="Re-enter password"
                      required
                    />
                    {confirmPass && (
                      <span className={`material-symbols-outlined text-sm pr-3 ${
                        confirmPass === newPassword ? 'text-emerald-500' : 'text-red-400'
                      }`}>
                        {confirmPass === newPassword ? 'check_circle' : 'cancel'}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !newPassword || newPassword !== confirmPass}
                  className="w-full py-2 bg-gradient-to-r from-primary to-primary-container text-white font-headline font-bold text-xs rounded-xl shadow-md flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Resetting…</>
                    : <><span className="material-symbols-outlined text-sm">lock_reset</span> Reset Password</>
                  }
                </button>
              </form>
            )}

            {/* Footer */}
            <div className="mt-auto pt-4 border-t border-surface-container-high text-center">
              <div className="flex items-center justify-center gap-1.5 text-[10px] text-secondary mb-2">
                <span className="material-symbols-outlined" style={{ fontSize: '12px', fontVariationSettings: "'FILL' 1" }}>
                  verified_user
                </span>
                SECURE HUB ENCRYPTION
              </div>
              <Link to="/login" className="inline-flex items-center gap-1 text-[11px] text-primary font-bold hover:underline">
                <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>arrow_back</span>
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;